import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { SidebarSectionToggle } from "@/components/_templates/sidebar/sidebar-section-toggle";
import { useCheckPermission } from "@/domain/_shared/permission/hooks/api/use-check-permission";
import { useTeamListForWorkspace } from "@/domain/team/hooks/api/use-team-list-for-workspace";
import { CurrentUser } from "@/domain/user/models/current-user-model";
import { Workspace } from "@/domain/workspace/models/workspace-model";

import { resolveVisibleTeamList } from "./resolve-visible-team-list";
import { SidebarSettingsNoTeamCard } from "./sidebar-settings-no-team-card";
import { SidebarSettingsTeamNavElement } from "./sidebar-settings-team-nav-element";

interface SidebarSettingsAllTeamsNavElementProps {
  workspace: Workspace;
  user: CurrentUser;
}

export const SidebarSettingsAllTeamsNavElement = ({
  workspace,
  user,
}: SidebarSettingsAllTeamsNavElementProps) => {
  const [expanded, setExpanded] = useState(true);
  const { t } = useTranslation("common");
  const { teamId } = useParams();
  const { getTeamListForUser, teamList } = useTeamListForWorkspace({
    workspaceId: workspace.id,
  });
  const { hasPermission: hasPermissionToManageWorkspace, loading: permissionLoading } = useCheckPermission({
    resource: "workspace",
    action: "manage",
    resourceId: workspace.id,
  });

  const activeTeamListForCurrentUser = getTeamListForUser(user.id, {
    statusList: ["ACTIVE"],
  });

  // A workspace manager can reach any team's settings from the team-management
  // page even without being a member. Surface the team being viewed so they
  // have sidebar navigation (general / members) for it.
  const visibleTeamList = useMemo(
    () =>
      resolveVisibleTeamList({
        activeTeamList: activeTeamListForCurrentUser,
        teamList,
        teamId,
        canManageWorkspace: hasPermissionToManageWorkspace || user.superUser,
      }),
    [activeTeamListForCurrentUser, hasPermissionToManageWorkspace, user.superUser, teamId, teamList],
  );

  return (
    <>
      <SidebarSectionToggle
        label={t("sidebar.myTeams")}
        expanded={expanded}
        onToggle={() => {
          setExpanded(!expanded);
        }}
      />
      {expanded && (
        <>
          {visibleTeamList.map((team, index) => {
            const shouldExpandByDefault = index === 0 || team.id === teamId;
            return (
              <SidebarSettingsTeamNavElement
                key={team.id}
                workspace={workspace}
                team={team}
                expanded={shouldExpandByDefault}
              />
            );
          })}
          {!permissionLoading && visibleTeamList.length === 0 && <SidebarSettingsNoTeamCard />}
        </>
      )}
    </>
  );
};
